import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { paqueteDePais, type PaqueteDePais } from '@actualgold/localizacion';
import { guardarToken, pedir, recuperarSesion, avisarCuandoSePierdaLaSesion } from '../api/cliente';

export interface EmpresaDeLaPersona {
  readonly empresaId: string;
  readonly nombre: string;
  readonly rolId: string;
  readonly activa: boolean;
  /** Suspendida por la plataforma: se muestra como lo que es y no se deja elegir. */
  readonly suspendida: boolean;
  /** Qué es el comercio frente al fisco: la mitad de lo que decide la letra del comprobante. */
  readonly condicionIva: string;
  /** Su país, que dice con qué paquete se lee todo lo fiscal (E35). */
  readonly paisCodigo: string;
}

export interface Perfil {
  readonly id: string;
  readonly correo: string;
  readonly nombre: string;
  readonly apellido: string | null;
  readonly idioma: string;
  readonly zonaHoraria: string;
  /**
   * Quién administra la instalación. Decide a dónde va esta persona al entrar y si ve
   * «Plataforma» en el menú. **No abre nada**: las rutas de plataforma consultan la base en
   * cada pedido, y esto es una copia para orientar la pantalla.
   */
  readonly esAdministradorDePlataforma: boolean;
  /** Entró con una clave provisoria: se le pide elegir la suya antes que nada. */
  readonly debeCambiarClave: boolean;
  readonly segundoFactorActivo: boolean;
  readonly empresas: readonly EmpresaDeLaPersona[];
}

export type EstadoDeSesion = 'cargando' | 'sin_sesion' | 'sin_empresa' | 'lista';

interface Sesion {
  readonly estado: EstadoDeSesion;
  readonly perfil: Perfil | null;
  readonly empresaActivaId: string | null;
  readonly permisos: ReadonlySet<string>;
  readonly entrar: (token: string) => Promise<void>;
  readonly pararseEn: (empresaId: string) => Promise<void>;
  readonly salir: () => Promise<void>;
  readonly recargarPerfil: () => Promise<void>;
}

const ContextoDeSesion = createContext<Sesion | null>(null);

/**
 * Estado de la sesión para toda la aplicación.
 *
 * Modela **tres estados y no dos**: sin sesión, con sesión pero sin empresa elegida, y
 * lista para operar. El del medio existe de verdad —quien acaba de aceptar una invitación
 * de fundación todavía no tiene empresa— y tratarlo como un caso raro del primero es lo
 * que produce pantallas que parpadean o menús que aparecen vacíos.
 */
export function ProveedorDeSesion({ children }: { children: React.ReactNode }): React.ReactElement {
  const [estado, setEstado] = useState<EstadoDeSesion>('cargando');
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [empresaActivaId, setEmpresaActivaId] = useState<string | null>(null);
  const [permisos, setPermisos] = useState<ReadonlySet<string>>(new Set());
  // Lo que las pantallas ya pidieron y guardaron —listas, fichas, la lista de comercios de la
  // plataforma— **es de la sesión que lo pidió**. Se vacía al salir, al perder la sesión, al
  // entrar y al cambiar de comercio: sin esto, quien entraba después en la misma pestaña veía
  // durante un instante lo del anterior, y en la plataforma —donde el pedido nuevo es rechazado y
  // la caché se queda con lo viejo— lo veía para siempre. Pasó en producción.
  const cola = useQueryClient();

  const cargarPerfil = useCallback(async (): Promise<Perfil> => {
    const nuevo = await pedir<Perfil>('/acceso/yo');
    setPerfil(nuevo);
    return nuevo;
  }, []);

  const pararseEn = useCallback(
    async (empresaId: string): Promise<void> => {
      const respuesta = await pedir<{ tokenDeAcceso: string; permisos: string[] }>(
        '/acceso/empresa',
        { method: 'POST', cuerpo: { empresaId } },
      );
      guardarToken(respuesta.tokenDeAcceso);
      cola.clear();
      setEmpresaActivaId(empresaId);
      setPermisos(new Set(respuesta.permisos));
      globalThis.localStorage?.setItem('actualgold.empresa', empresaId);
      setEstado('lista');
    },
    [cola],
  );

  const acomodarse = useCallback(
    async (nuevo: Perfil): Promise<void> => {
      // Activa la membresía **y** no suspendido el comercio: entrar solo a uno suspendido daría
      // un rechazo que la pantalla ya podía anticipar.
      const activas = nuevo.empresas.filter((empresa) => empresa.activa && !empresa.suspendida);
      if (activas.length === 0) {
        setEstado('sin_empresa');
        return;
      }

      // Se vuelve a la última empresa usada; si es una sola, se entra derecho. Elegir
      // entre una sola opción es una pantalla que no le sirve a nadie.
      const ultima = globalThis.localStorage?.getItem('actualgold.empresa');
      const preferida =
        activas.find((empresa) => empresa.empresaId === ultima) ??
        (activas.length === 1 ? activas[0] : undefined);

      if (preferida === undefined) {
        setEstado('sin_empresa');
        return;
      }
      await pararseEn(preferida.empresaId);
    },
    [pararseEn],
  );

  const entrar = useCallback(
    async (token: string): Promise<void> => {
      guardarToken(token);
      cola.clear();
      await acomodarse(await cargarPerfil());
    },
    [acomodarse, cargarPerfil, cola],
  );

  const salir = useCallback(async (): Promise<void> => {
    await pedir('/acceso/salida', { method: 'POST' }).catch(() => undefined);
    guardarToken(null);
    setPerfil(null);
    setEmpresaActivaId(null);
    setPermisos(new Set());
    setEstado('sin_sesion');
    cola.clear();
  }, [cola]);

  useEffect(() => {
    avisarCuandoSePierdaLaSesion(() => {
      setPerfil(null);
      setEmpresaActivaId(null);
      setPermisos(new Set());
      setEstado('sin_sesion');
      cola.clear();
    });

    void (async () => {
      // Al abrir la aplicación se intenta renovar con la cookie: si la sesión sigue viva,
      // nadie tiene que volver a escribir su contraseña.
      if (!(await recuperarSesion())) {
        setEstado('sin_sesion');
        return;
      }
      try {
        await acomodarse(await cargarPerfil());
      } catch {
        setEstado('sin_sesion');
      }
    })();
  }, [acomodarse, cargarPerfil, cola]);

  const valor = useMemo<Sesion>(
    () => ({
      estado,
      perfil,
      empresaActivaId,
      permisos,
      entrar,
      pararseEn,
      salir,
      recargarPerfil: async () => {
        await cargarPerfil();
      },
    }),
    [estado, perfil, empresaActivaId, permisos, entrar, pararseEn, salir, cargarPerfil],
  );

  return <ContextoDeSesion value={valor}>{children}</ContextoDeSesion>;
}

/**
 * Los prefijos que son contrato del framework —`use`, `on`— van en inglés, igual que
 * `Repository` o `Module` en el servidor (ADR-0004). El sustantivo sigue siendo del
 * negocio.
 */
export function useSesion(): Sesion {
  const sesion = use(ContextoDeSesion);
  if (sesion === null) {
    throw new Error('useSesion fuera del ProveedorDeSesion');
  }
  return sesion;
}

/**
 * ¿La persona puede hacer esta operación?
 *
 * Se usa para **esconder o deshabilitar** lo que no corresponde. Nunca es la única
 * defensa: la API rechaza igual (INV-18). Esconder un botón es cortesía; el permiso lo
 * verifica el servidor.
 */
export function usePermiso(codigo: string): boolean {
  return useSesion().permisos.has(codigo);
}

/**
 * Qué es, frente al fisco, la empresa en la que se está parado.
 *
 * Sin empresa activa devuelve `null`, y quien lo use tiene que contemplarlo: es el estado real
 * de quien acaba de entrar y todavía no eligió dónde trabajar.
 */
export function useCondicionDeLaEmpresa(): string | null {
  const { perfil, empresaActivaId } = useSesion();
  return (
    perfil?.empresas.find((empresa) => empresa.empresaId === empresaActivaId)?.condicionIva ?? null
  );
}

/**
 * El paquete del país de la empresa en la que se está parado: qué tipos de identificación y qué
 * condiciones ofrecer, y si hay letra y fisco electrónico. Sin empresa activa, `null`.
 */
export function usePaqueteDeLaEmpresa(): PaqueteDePais | null {
  const { perfil, empresaActivaId } = useSesion();
  const pais = perfil?.empresas.find(
    (empresa) => empresa.empresaId === empresaActivaId,
  )?.paisCodigo;
  return pais === undefined ? null : (paqueteDePais(pais) ?? null);
}
